import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { cloneDeep } from 'lodash'
import { refetchFunc } from 'modules/domain/common/hooks'
import { DutyType, Export, ExportDocumentType } from 'modules/domain/export/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { ExportFormFields } from './AddExport'
import { CommonFields } from './CommonFields'

type Props = {
  refetch: refetchFunc
  onClose: () => void
  exportData: Export
}

export const EditExport: FC<Props> = ({ onClose, exportData, refetch }) => {
  const { t } = useTranslation('export')

  const { formProgress, formHandler } = useAFormHandler('addExportContract')

  const filterDocumentsByType = (type: ExportDocumentType) => {
    return exportData.documents.filter(({ document_type }) => document_type === type)
  }

  const formik = useFormik<ExportFormFields>({
    initialValues: {
      responsible_coordinator: exportData.responsible_coordinator
        ? exportData.responsible_coordinator.id.toString()
        : null,
      product: exportData.product?.id.toString(),
      quantity: exportData.quantity,
      delivery_basis: exportData.delivery_basis,
      number: exportData.number,
      unique_contract_number: exportData.unique_contract_number,
      contract_date: exportData.contract_date,
      consignment_date: exportData.consignment_date,
      through_dubai: exportData.through_dubai,
      through_dubai_price: exportData.through_dubai_price,
      through_dubai_price_currency: exportData.through_dubai_price_currency,
      purchaser: exportData.purchaser?.id.toString(),
      ship: exportData.ship?.id.toString(),
      ship_owner: exportData.ship_owner?.id.toString(),
      price: exportData.price,
      price_currency: exportData.price_currency,
      freight_cost: exportData.freight_cost,
      freight_currency: exportData.freight_currency,
      duty_type: exportData.duty_type,
      duty_value:
        exportData.duty_type === DutyType.FIXED ? exportData.fix_duty_value : exportData.percentage_duty_value,
      storage_cost: exportData.storage_cost,
      storage_currency: exportData.storage_currency,
      documents_cost: exportData.documents_cost,
      documents_currency: exportData.documents_currency,
      warehouse: exportData.warehouse?.id.toString(),
      warehouse_cost: exportData.warehouse_cost,
      warehouse_currency: exportData.warehouse_currency,
      freight_broker: exportData.freight_broker?.id.toString(),
      freight_broker_cost: exportData.freight_broker_cost,
      freight_broker_currency: exportData.freight_broker_currency,
      export_broker: exportData.export_broker?.id.toString(),
      export_broker_cost: exportData.export_broker_cost,
      export_broker_currency: exportData.export_broker_currency,
      insurance: exportData.insurance?.id.toString(),
      insurance_cost: exportData.insurance_cost,
      insurance_currency: exportData.insurance_currency,
      surveyor: exportData.surveyor?.id.toString(),
      surveyor_cost: exportData.surveyor_cost,
      surveyor_currency: exportData.surveyor_currency,
      forwarder: exportData.forwarder?.id,
      forwarder_cost: exportData.forwarder_cost,
      forwarder_currency: exportData.forwarder_currency,
      other_mark: exportData.other_mark?.id,

      // documents
      contractDocuments: filterDocumentsByType(ExportDocumentType.CONTRACT),
      unkDocuments: filterDocumentsByType(ExportDocumentType.UNK),
      shipOwnerDocuments: filterDocumentsByType(ExportDocumentType.SHIP_OWNER),
      freightBrokerDocuments: filterDocumentsByType(ExportDocumentType.FREIGHT_BROKER),
      exportBrokerDocuments: filterDocumentsByType(ExportDocumentType.EXPORT_BROKER),
      insuranceDocuments: filterDocumentsByType(ExportDocumentType.INSURANCE),
      surveyorDocuments: filterDocumentsByType(ExportDocumentType.SURVEYOR),
      forwarderDocuments: filterDocumentsByType(ExportDocumentType.FORWARDER),
    },
    onSubmit: formHandler(
      async () => {
        const valuesToSend = cloneDeep(formik.values)
        delete valuesToSend.duty_value

        const documentFields = [
          'contractDocuments',
          'unkDocuments',
          'shipOwnerDocuments',
          'freightBrokerDocuments',
          'exportBrokerDocuments',
          'insuranceDocuments',
          'surveyorDocuments',
          'forwarderDocuments',
        ]

        const files = documentFields.reduce((acc, field) => {
          const documents = valuesToSend[field].map(({ document_type, id }) => ({ document_type, id }))
          acc = acc.concat(documents)
          delete valuesToSend[field]
          return acc
        }, [])
        const editableDocTypes = [
          'contract',
          'unk',
          'ship_owner',
          'freight_broker',
          'export_broker',
          'insurance',
          'surveyor',
          'forwarder',
        ]
        const nonEditableFiles = exportData.documents.filter(
          (doc) => !editableDocTypes.includes(doc.document_type ?? ''),
        )

        valuesToSend.form_files = [...nonEditableFiles, ...files]

        if (formik.values.duty_type === DutyType.FIXED) {
          valuesToSend.fix_duty_value = formik.values.duty_value
        }

        if (formik.values.duty_type === DutyType.PERCENT) {
          valuesToSend.percentage_duty_value = formik.values.duty_value
        }

        await apiClient.put(endpoints.export(exportData.id), valuesToSend)
      },
      {
        onSuccess: () => {
          onClose()
          refetch()
        },
        onError: (error) => {
          const { errors } = error
          formik.setErrors(errors)
        },
      },
    ),
  })

  return (
    <ResponsiveModal
      onConfirm={() => formik.submitForm()}
      confirmButtonText={t('common:save')}
      title={t('newExportContract')}
      progress={formProgress}
      onClose={onClose}
      id="addExport"
    >
      <CommonFields formik={formik} exportData={exportData} />
    </ResponsiveModal>
  )
}
